import React from 'react';
import { FormContext } from 'react-hook-form';
import { ListRenderItem, ScrollView } from 'react-native';
import { Modal, ProgressBar } from 'react-native-paper';
import { t, Trans } from '@lingui/macro';
import { useNavigation } from '@react-navigation/native';

import Button from '@src/components/Button';
import Container from '@src/components/Container';
import PrimeBookReviewCard from '@src/components/PrimeBookReviewCard';
import RefreshControl from '@src/components/RefreshControl';
import Score from '@src/components/Score';
import Separator from '@src/components/Separator';
import { Body1, H2, Subtitle1, Subtitle2 } from '@src/components/Typography';
import VisibleStatusBar from '@src/components/VisibleStatusBar';
import useAnimatedHeader from '@src/hooks/useAnimatedHeader';
import { i18n } from '@src/i18n';
import { PrimeMedia, Review } from '@src/types';

import PrimeReview from './PrimeReview';
import {
  CenteredColumn,
  CenteredRow,
  Column,
  Content,
  LargeText,
  Cover,
  MediaPreview,
  MediaPreviewList,
  ReviewButton,
  ReviewList,
  Row,
  Star,
  Surface,
  SampleButton,
  SampleRow,
  CoverContainer,
} from './styled';
import { Props } from './types';
import useDownloadPages from './useDownloadPages';
import usePrimeBook from './usePrimeBook';
import usePrimeBookReviews from './usePrimeBookReviews';
import usePrimeMediasPreviews from './usePrimeMediasPreviews';
import useSubmitReview from './useSubmitReview';

const Screen: React.FC<Props> = ({
  route: {
    params: { id },
  },
}) => {
  const handleScroll = useAnimatedHeader();
  const navigation = useNavigation();
  const primeBook = usePrimeBook(id);
  const reviews = usePrimeBookReviews(id);
  const mediaPreviews = usePrimeMediasPreviews(id);
  const { form, handleSubmit, loading } = useSubmitReview(id);
  const [visible, setVisible] = React.useState(false);

  React.useLayoutEffect(() => {
    navigation.setOptions({
      title: primeBook.data
        ? `${primeBook.data.title} - ${i18n._(t`Youtopin`)}`
        : undefined,
      headerTitle: primeBook.data?.title ?? '',
    });
  }, [navigation, primeBook.data]);

  const downloadPages = useDownloadPages(primeBook.data);

  const openModalHandler = () => {
    setVisible(true);
  };

  const closeModalHandler = () => {
    setVisible(false);
  };

  const submitReviewHandler = React.useCallback(() => {
    handleSubmit().then(() => closeModalHandler());
  }, [handleSubmit]);

  const renderReviewItem: ListRenderItem<Review> = React.useCallback(
    ({ item }) => (
      <PrimeBookReviewCard
        user={item.user}
        description={item.comment}
        rate={item.rate}
      />
    ),
    []
  );

  const renderMediaPreviewItem: ListRenderItem<PrimeMedia> = React.useCallback(
    ({ item }) => (
      <Surface>
        <MediaPreview source={item?.media?.source} />
      </Surface>
    ),
    []
  );

  return (
    <>
      <VisibleStatusBar />

      <Container>
        <ScrollView onScroll={handleScroll}>
          <Content>
            <Row>
              <CenteredColumn>
                <CoverContainer>
                  <Cover
                    source={
                      primeBook.data?.cover.source ??
                      require('@src/assets/images/cover.png')
                    }
                  />
                </CoverContainer>
                <Separator />
                <SampleRow align="space-between">
                  <LargeText textColor={'#757575'}>
                    <Trans>score</Trans>
                    {': '}
                  </LargeText>
                  <Score
                    value={primeBook.data?.score ?? 0}
                    size={14}
                    color="#ffc23a"
                  >
                    <Star />
                  </Score>
                </SampleRow>
                <SampleRow align="flex-end">
                  <LargeText textColor={'#757575'}>
                    <Trans>{primeBook.data?.scoreCount} Rating(s)</Trans>
                  </LargeText>
                </SampleRow>
                <Separator />
                <ReviewButton onPress={openModalHandler}>
                  <Trans>Submit your review</Trans>
                </ReviewButton>
              </CenteredColumn>
              <Separator />
              <Column>
                {/* <H2>{primeBook.data?.title}</H2> */}
                <Separator />
                {primeBook.data?.publisher && (
                  <SampleRow align="start">
                    <LargeText textColor={'#757575'}>
                      <Trans>Publisher:</Trans>
                    </LargeText>
                    <LargeText isBold={true}>
                      {primeBook.data?.publisher}
                    </LargeText>
                  </SampleRow>
                )}
                <Separator />
                {primeBook.data?.translator && (
                  <SampleRow align="start">
                    <LargeText textColor={'#757575'}>
                      <Trans>Translator:</Trans>
                    </LargeText>
                    <LargeText isBold={true}>
                      {primeBook.data?.translator}
                    </LargeText>
                  </SampleRow>
                )}
                <Separator />
                <SampleRow align="start">
                  <LargeText textColor={'#757575'}>
                    <Trans>Type of Guide:</Trans>
                  </LargeText>
                  <LargeText isBold={true}>
                    {primeBook.data?.type === 1 ? (
                      <Trans>Text</Trans>
                    ) : primeBook.data?.type === 2 ? (
                      <Trans>Audio</Trans>
                    ) : (
                      <Trans>Text & Audio</Trans>
                    )}
                  </LargeText>
                </SampleRow>
              </Column>
            </Row>
            <Separator />

            <Button
              mode="contained"
              onPress={downloadPages.handleDownload}
              disabled={downloadPages.loading !== -1}
            >
              <Trans>Access to full version</Trans>
            </Button>
            <Separator />
            {/* <CenteredRow>
              <SampleButton>
                <SampleRow>
                  <Trans>Sample Audio</Trans>
                  <ContentIcon
                    source={require('@src/assets/images/play.png')}
                  />
                </SampleRow>
              </SampleButton>

              <SampleButton>
                <SampleRow>
                  <Trans>Sample Text</Trans>
                  <ContentIcon
                    source={require('@src/assets/images/book.png')}
                  />
                </SampleRow>
              </SampleButton>
            </CenteredRow>
            <Separator /> */}
            <ProgressBar
              visible={downloadPages.loading !== -1}
              indeterminate={downloadPages.loading === 0}
            />
            <H2>
              <Trans>Description</Trans>
            </H2>
            <Body1>{primeBook.data?.description}</Body1>
          </Content>
          <Content>
            <H2>
              <Trans>Media Previews</Trans>
            </H2>
          </Content>
          <MediaPreviewList
            data={mediaPreviews.data}
            onEndReached={mediaPreviews.handleEndReached}
            renderItem={renderMediaPreviewItem}
            refreshControl={
              <RefreshControl
                refreshing={mediaPreviews.refreshing}
                onRefresh={mediaPreviews.handleRefresh}
              />
            }
          />
          <Separator />
          <Content>
            <H2>
              <Trans>User Reviews</Trans>
            </H2>
          </Content>
          <ReviewList
            data={reviews.data}
            onEndReached={reviews.handleEndReached}
            renderItem={renderReviewItem}
            refreshControl={
              <RefreshControl
                refreshing={reviews.refreshing}
                onRefresh={reviews.handleRefresh}
              />
            }
          />
        </ScrollView>
      </Container>
      <Modal visible={visible} dismissable onDismiss={closeModalHandler}>
        <FormContext {...form}>
          <PrimeReview loading={loading} onSubmit={submitReviewHandler} />
        </FormContext>
      </Modal>
    </>
  );
};

Screen.displayName = 'PrimeBook';

export default Screen;
